<template>
  <div class="fixed inset-0 bg-black/50 tw-flex items-center justify-center" style="z-index: 9999;" @click.self="$emit('close')">
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
      <!-- Header -->
      <div class="tw-flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">{{ $t('knowledge_base_contributors') }}</h2>
          <p class="text-sm text-gray-500 mt-0.5">{{ $t('knowledge_base_contributors_description') }}</p>
        </div>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
          <Icon name="x" class="w-5 h-5"/>
        </button>
      </div>

      <!-- Search -->
      <div class="px-6 py-4 border-b border-gray-100">
        <div class="relative">
          <DsInput
            is-simplified
            icon="search"
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            :placeholder="$t('knowledge_base_search_users_placeholder')"
            @input="onSearchInput"
          />
          <div v-if="searching" class="absolute right-3 top-1/2 -translate-y-1/2">
            <Icon name="spinner" class="w-4 h-4 text-gray-400 animate-spin"/>
          </div>
        </div>

        <!-- Search results dropdown -->
        <div v-if="searchResults.length > 0" class="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-48 overflow-y-auto">
          <button
            v-for="user in searchResults"
            :key="user.id"
            class="w-full tw-flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
            :disabled="isAlreadyContributor(user.id)"
            @click="addUser(user)"
          >
            <div class="w-8 h-8 rounded-full bg-primary-100 tw-flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img v-if="user.photo_url" :src="user.photo_url" :alt="user.name" class="w-full h-full object-cover"/>
              <span v-else class="text-xs font-medium text-primary-700">{{ initials(user.name) }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900 truncate">{{ user.name }}</div>
              <div class="text-xs text-gray-500 truncate">{{ user.email }}</div>
            </div>
            <span v-if="isAlreadyContributor(user.id)" class="text-xs text-gray-400">{{ $t('knowledge_base_already_added') }}</span>
            <Icon v-else name="plus" class="w-4 h-4 text-primary-600 flex-shrink-0"/>
          </button>
        </div>
        <p v-else-if="searchQuery.length >= 2 && !searching && searchResults.length === 0" class="text-xs text-gray-500 mt-2">
          {{ $t('knowledge_base_no_users_found') }}
        </p>
      </div>

      <!-- Current contributors -->
      <div class="px-6 py-4 max-h-72 overflow-y-auto">
        <ul v-if="allPeople.length > 0" class="space-y-2">
          <li v-for="user in allPeople" :key="user.id" class="tw-flex items-center gap-3">
            <div class="w-8 h-8 rounded-full bg-primary-100 tw-flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img v-if="user.photo_url" :src="user.photo_url" :alt="user.name" class="w-full h-full object-cover"/>
              <span v-else class="text-xs font-medium text-primary-700">{{ initials(user.name) }}</span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-900 truncate">{{ user.name }}</div>
              <div class="text-xs text-gray-500 truncate">{{ user.email }}</div>
            </div>
            <button
              v-if="canManage && contributors.some((c: any) => c.id === user.id) && user.id !== creator?.id && user.id !== currentUserId"
              @click="removeUser(user.id)"
              class="text-gray-400 hover:text-red-500 p-1 rounded transition-colors"
              :title="$t('knowledge_base_remove_contributor')"
            >
              <Icon name="x" class="w-4 h-4"/>
            </button>
          </li>
        </ul>
        <div v-else class="text-sm text-gray-500 text-center py-4">
          {{ $t('knowledge_base_no_contributors_yet') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import { searchUser } from '@/api/config'
  import { addContributor, removeContributor } from '@/api/knowledge-base'
  import { fetchMembers } from '@/api/communities'
  import DsInput from '@/components/Form/DsInput.vue'
  import TranslationsMixin from '@/util/TranslationsMixin'

  export default defineComponent({
    name: 'KnowledgeBaseContributorsModal',
    mixins: [TranslationsMixin],
    components: { DsInput, Icon },
    props: {
      postId: {
        type: Number,
        required: true,
      },
      contributors: {
        type: Array as () => any[],
        default: () => [],
      },
      communityManagers: {
        type: Array as () => any[],
        default: () => [],
      },
      canManage: {
        type: Boolean,
        default: false,
      },
      communityId: {
        type: Number,
        default: null,
      },
      creator: {
        type: Object as () => any,
        default: null,
      },
      currentUserId: {
        type: Number,
        default: null,
      },
    },
    emits: ['close', 'contributors-updated'],
    computed: {
      allPeople(): any[] {
        const seen = new Set<number>()
        const result: any[] = []
        const add = (p: any) => { if (p?.id && !seen.has(p.id)) { seen.add(p.id); result.push(p) } }
        if (this.creator) add(this.creator)
        this.contributors.forEach(add)
        this.communityManagers.forEach(add)
        return result
      },
    },
    data() {
      return {
        searchQuery: '',
        searchResults: [] as any[],
        searching: false,
        searchTimeout: null as ReturnType<typeof setTimeout> | null,
        communityMembers: [] as any[],
      }
    },
    async mounted() {
      this.$nextTick(() => {
        (this.$refs.searchInput as HTMLInputElement)?.focus()
      })
      if (this.communityId) {
        try {
          const data = await fetchMembers(this.communityId)
          this.communityMembers = Array.isArray(data) ? data : (data?.members || data?.data || [])
        } catch (e) {
          this.communityMembers = []
        }
      }
    },
    methods: {
      initials(name: string): string {
        return name
          ?.split(' ')
          .map((n: string) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2) || '?'
      },
      isAlreadyContributor(userId: number): boolean {
        return this.creator?.id === userId || this.contributors.some((c: any) => c.id === userId)
      },
      onSearchInput() {
        if (this.searchTimeout) clearTimeout(this.searchTimeout)
        if (this.searchQuery.length < 2) {
          this.searchResults = []
          return
        }
        this.searchTimeout = setTimeout(() => this.doSearch(), 300)
      },
      async doSearch() {
        if (this.communityId) {
          const q = this.searchQuery.toLowerCase()
          this.searchResults = this.communityMembers.filter(m =>
            m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)
          )
          return
        }
        this.searching = true
        try {
          const results = await searchUser({ query: this.searchQuery })
          this.searchResults = results || []
        } catch (e) {
          this.searchResults = []
        } finally {
          this.searching = false
        }
      },
      async addUser(user: any) {
        if (this.isAlreadyContributor(user.id)) return
        try {
          await addContributor(this.postId, user.id)
          this.$emit('contributors-updated')
          this.searchQuery = ''
          this.searchResults = []
        } catch (e) {
          console.error('Failed to add contributor', e)
        }
      },
      async removeUser(userId: number) {
        try {
          await removeContributor(this.postId, userId)
          this.$emit('contributors-updated')
        } catch (e) {
          console.error('Failed to remove contributor', e)
        }
      },
    },
  })
</script>
